import React, { useState, useEffect } from 'react';
import LogicMaze from './LogicMaze';
import ProofBuilder from './ProofBuilder';
import PrivacyShowdown from './PrivacyShowdown';
import PrivacyQuiz from './PrivacyQuiz';
import TutorialModule from './TutorialModule';
import VisualTutorial from './VisualTutorial';
import ProgressIndicator from './ProgressIndicator';
import MatchingGame from './MatchingGame';
import FlowArrangement from './FlowArrangement';
import StackQuest from './StackQuest';
import VMExecutionLesson from './VMExecutionLesson';
import FibonacciProofLesson from './FibonacciProofLesson';
import { useProgress } from '../contexts/ProgressContext';
import { tutorials } from '../data/tutorials';

const Playground = ({ code, output, onCodeChange, isPlaygroundPage = false, setCurrentPage, setIsSidebarCollapsed }) => {
  const [activeTab, setActiveTab] = useState('games');
  const [currentGame, setCurrentGame] = useState(null);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [currentExperiment, setCurrentExperiment] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const { getProgress } = useProgress();

  // Auto-close sidebar when a game or tutorial is opened
  useEffect(() => {
    if (currentGame || selectedTutorial || currentExperiment || currentChallenge) {
      if (setIsSidebarCollapsed) {
        setIsSidebarCollapsed(true);
      }
    }
  }, [currentGame, selectedTutorial, currentExperiment, currentChallenge, setIsSidebarCollapsed]);

  // Reset game/tutorial state when navigating to playground from sidebar
  // This allows the Playground button in sidebar to work even when in a game
  useEffect(() => {
    if (isPlaygroundPage && !currentGame && !selectedTutorial) {
      // Already on playground with no active game/tutorial - ensure sidebar can be opened
      // This effect ensures state is clean when navigating here
    }
  }, [isPlaygroundPage, currentGame, selectedTutorial]);

  // Helper function to get game progress
  const getGameProgress = (gameId, totalLevels) => {
    try {
      const gameState = getProgress('games', gameId);
      if (!gameState) return `0 of ${totalLevels}`;
      
      const completed = Boolean(gameState?.completed);
      const level = typeof gameState?.level === 'number' ? gameState.level : 0;
      
      // If game is marked as completed, show all levels done
      if (completed) {
        return `${totalLevels} of ${totalLevels}`;
      }
      
      const done = Math.min(level, totalLevels);
      
      return `${done} of ${totalLevels}`;
    } catch (_) {
      return `0 of ${totalLevels}`;
    }
  };

  // Get progress for all games
  const matchingGameProgressText = getGameProgress('matching-game', 4);
  const flowArrangementProgressText = getGameProgress('flow-arrangement', 4);
  const logicMazeProgressText = getGameProgress('logic-maze', 6);
  const proofBuilderProgressText = getGameProgress('proof-builder', 3);
  const privacyShowdownProgressText = getGameProgress('privacy-showdown', 5);
  const privacyQuizProgressText = getGameProgress('privacy-quiz', 10);
  const stackQuestProgressText = getGameProgress('stack-quest', 8);

  const activities = {
    games: [
      {
        id: 'matching-game',
        title: 'Match the Concepts',
        description: 'Match real-world privacy scenarios with Miden concepts. Learn how actions stay private while being verifiable. 3-5 minutes.',
        objective: 'Match each human description to its correct Miden concept',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 8h8M8 12h8M8 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          </svg>
        ),
        progress: matchingGameProgressText,
        difficulty: 'Beginner',
        timeEstimate: '3-5 min',
        concept: 'Client-Side Proving',
        visualUI: true
      },
      {
        id: 'flow-arrangement',
        title: 'Arrange the Flow',
        description: 'Arrange actors and steps to show how private transactions flow from wallet to blockchain. Learn who sees what. 3-5 minutes.',
        objective: 'Arrange tiles showing actors and steps: wallet prepares → VM runs → proof created → network verifies',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        progress: flowArrangementProgressText,
        difficulty: 'Beginner',
        timeEstimate: '3-5 min',
        concept: 'Transaction Flow',
        visualUI: true
      },
      {
        id: 'logic-maze',
        title: 'Logic Maze',
        description: 'Master zero-knowledge proofs through interactive logic puzzles. Choose your theme: Cyber Vault Heist, Circuit Builder, or Cipher Adventure. 5-10 minutes.',
        objective: 'Solve logic puzzles by satisfying zero-knowledge constraints. Each level tests your understanding of ZK proofs.',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="4" rx="2" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="10" width="18" height="4" rx="2" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="17" width="18" height="4" rx="2" stroke="currentColor" strokeWidth="2"/>
          </svg>
        ),
        progress: logicMazeProgressText,
        difficulty: 'Intermediate',
        timeEstimate: '5-10 min',
        concept: 'Private Proofs',
        visualUI: true
      },
      {
        id: 'proof-builder',
        title: 'Build a Private Receipt',
        description: 'Build a private receipt for a real action. Prove you sent a payment or recovered an account without revealing the details. 5-8 minutes.',
        objective: 'Build a private receipt that proves an action happened without revealing sensitive details',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          </svg>
        ),
        progress: proofBuilderProgressText,
        difficulty: 'Beginner',
        timeEstimate: '5-8 min',
        concept: 'Private Proofs',
        visualUI: true
      },
      {
        id: 'privacy-showdown',
        title: 'Spot the Leak',
        description: 'Compare two transaction flows and spot which one leaks more. Learn how Miden keeps actions private. 3-5 minutes per scenario.',
        objective: 'Identify which flow leaks more private information',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ),
        progress: privacyShowdownProgressText,
        difficulty: 'Beginner',
        timeEstimate: '3-5 min',
        concept: 'Privacy Protection'
      },
      {
        id: 'privacy-quiz',
        title: 'Privacy Knowledge',
        description: 'Test what you know about keeping actions private and secure. Questions about flows, roles, and what gets revealed. Quick feedback.',
        objective: 'Test your knowledge of private actions, secure receipts, and who can see what',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ),
        progress: privacyQuizProgressText,
        difficulty: 'Beginner',
        timeEstimate: '5-10 min',
        concept: 'Privacy Basics'
      },
      {
        id: 'stack-quest',
        title: 'Fix the Ledger',
        description: 'Fix broken accounts and failed transactions using private actions. Each scenario presents a problem—choose which private action fixes it without revealing transaction history. 10-15 minutes.',
        objective: 'Fix account problems by choosing the right private actions that keep secrets hidden',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 20V10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 20V4" stroke="currentColor" strokeWidth="2"/>
            <path d="M6 20v-6" stroke="currentColor" strokeWidth="2"/>
          </svg>
        ),
        progress: stackQuestProgressText,
        difficulty: 'Intermediate',
        timeEstimate: '10-15 min',
        concept: 'Account Recovery',
        visualUI: true
      }
    ],
    tutorials: tutorials.map(tutorial => ({
      id: tutorial.id,
      title: tutorial.title,
      description: tutorial.description,
      icon: tutorial.icon || (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      progress: `${tutorial.steps?.length || 0} steps`,
      difficulty: tutorial.difficulty || 'Beginner',
      visualTutorial: tutorial.visualTutorial || false
    })),
    experiments: [
      {
        id: 'vm-execution',
        title: 'Private Payment · Micro‑Lesson',
        description:
          'Walk through a short story of sending a private payment, then open the official Miden Playground to see how a simple payment program looks in real assembly.',
        objective:
          'Understand how a private payment creates a secure receipt without revealing all details.',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" />
            <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" />
            <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" />
            <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" />
          </svg>
        ),
        progress: 'Not started',
        difficulty: 'Beginner',
        timeEstimate: '3-5 min',
        concept: 'Private Payments',
        level: 1,
        forDevs: true,
        requiresCodeEditor: false
      },
      {
        id: 'state-analyzer',
        title: 'Fix the Ledger · Micro‑Lesson',
        description:
          'Follow a step‑by‑step scenario where an account is broken and then fixed using private actions, then jump into the official Playground to explore state‑style examples.',
        objective:
          'See how fixing an account can update things privately while still being verifiable.',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 20V10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 20V4" stroke="currentColor" strokeWidth="2" />
            <path d="M6 20v-6" stroke="currentColor" strokeWidth="2" />
          </svg>
        ),
        progress: 'Not started',
        difficulty: 'Intermediate',
        timeEstimate: '5-7 min',
        concept: 'Account Recovery',
        level: 1,
        forDevs: true,
        requiresCodeEditor: false
      }
    ],
    challenges: [
      {
        id: 'fibonacci-proof',
        title: 'Invisible Math · Challenge',
        description:
          'You see the result of a calculation, but not the steps. Read the scenario, answer a few questions, then open the official Miden Playground to explore real math examples like Fibonacci or primes.',
        objective:
          'Learn how you can prove a calculation was done correctly without revealing every step.',
        icon: (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
          </svg>
        ),
        progress: 'Not started',
        difficulty: 'Expert',
        timeEstimate: '7-10 min',
        concept: 'Invisible Math',
        level: 1,
        forDevs: true,
        requiresCodeEditor: false
      }
    ]
  };


  // Leaderboard data - 
  // const leaderboard = [
  //   { rank: 1, name: 'alice.miden', score: 120, progress: 85, badges: 15 },
  //   { rank: 2, name: 'bob.stark', score: 95, progress: 70, badges: 12 },
  //   { rank: 3, name: 'charlie.zk', score: 80, progress: 60, badges: 10 },
  //   { rank: 4, name: 'diana.proof', score: 65, progress: 45, badges: 8 },
  //   { rank: 5, name: 'eve.vm', score: 50, progress: 35, badges: 6 }
  // ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#10b981';
      case 'Intermediate': return '#f59e0b';
      case 'Advanced': return '#ef4444';
      case 'Expert': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const handleGameClick = (activityId) => {
    // Check if it's a tutorial
    const tutorial = tutorials.find(t => t.id === activityId);
    if (tutorial) {
      // Ensure visualTutorial flag is included
      setSelectedTutorial({
        ...tutorial,
        visualTutorial: tutorial.visualTutorial || false
      });
      return;
    }
    
    // Check if it's an experiment (micro-lessons)
    const experiment = activities.experiments?.find(e => e.id === activityId);
    if (experiment) {
      setCurrentExperiment(activityId);
      return;
    }
    
    // Check if it's a challenge
    const challenge = activities.challenges?.find(c => c.id === activityId);
    if (challenge) {
      setCurrentChallenge(activityId);
      return;
    }
    
    // Handle games (only actual games, not experiments/challenges)
    if (activityId === 'matching-game') {
      setCurrentGame('matching-game');
    } else if (activityId === 'flow-arrangement') {
      setCurrentGame('flow-arrangement');
    } else if (activityId === 'logic-maze') {
      setCurrentGame('logic-maze');
    } else if (activityId === 'proof-builder') {
      setCurrentGame('proof-builder');
    } else if (activityId === 'privacy-showdown') {
      setCurrentGame('privacy-showdown');
    } else if (activityId === 'privacy-quiz') {
      setCurrentGame('privacy-quiz');
    } else if (activityId === 'stack-quest') {
      setCurrentGame('stack-quest');
    }
  };

  const handleBackToPlayground = () => {
    setCurrentGame(null);
    setSelectedTutorial(null);
    setCurrentExperiment(null);
    setCurrentChallenge(null);
    // Optionally reopen sidebar when going back
    if (setIsSidebarCollapsed) {
      setIsSidebarCollapsed(false);
    }
  };

  if (isPlaygroundPage) {
    if (selectedTutorial) {
      const isVisualTutorial = selectedTutorial.visualTutorial || false;
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <div className="tutorial-page">
            {isVisualTutorial ? (
              <VisualTutorial tutorial={selectedTutorial} />
            ) : (
              <TutorialModule tutorial={selectedTutorial} />
            )}
          </div>
        </section>
      );
    }
    
    // If a specific game is selected, render that game
    if (currentGame === 'matching-game') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <MatchingGame />
        </section>
      );
    } else if (currentGame === 'flow-arrangement') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <FlowArrangement />
        </section>
      );
    } else if (currentGame === 'logic-maze') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <LogicMaze />
        </section>
      );
    } else if (currentGame === 'proof-builder') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <ProofBuilder />
        </section>
      );
    } else if (currentGame === 'privacy-showdown') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <PrivacyShowdown />
        </section>
      );
    } else if (currentGame === 'privacy-quiz') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <PrivacyQuiz />
        </section>
      );
    } else if (currentGame === 'stack-quest') {
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <StackQuest />
        </section>
      );
    }

    // Render experiments (micro-lessons)
    if (currentExperiment === 'vm-execution') {
      if (!VMExecutionLesson) {
        if (process.env.NODE_ENV === 'development') {
          console.error('VMExecutionLesson component not found');
        }
        return <div>Error: VMExecutionLesson component not loaded</div>;
      }
      return (
        <VMExecutionLesson onBack={handleBackToPlayground} />
      );
    } else if (currentExperiment === 'state-analyzer') {
      const experiment = activities.experiments.find(e => e.id === 'state-analyzer');
      return (
        <section className="playground-page">
          <div className="game-header">
            <button onClick={handleBackToPlayground} className="back-button">
              ← Back to Playground
            </button>
          </div>
          <div className="tutorial-page" style={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ color: '#e2e8f0', marginBottom: '24px', fontSize: '32px' }}>{experiment.title}</h1>
            <div style={{ 
              background: 'rgba(15, 23, 42, 0.6)', 
              border: '1px solid rgba(100, 116, 139, 0.3)', 
              borderRadius: '12px', 
              padding: '30px',
              marginBottom: '30px'
            }}>
              <h2 style={{ color: '#e2e8f0', marginBottom: '16px', fontSize: '20px' }}>The Scenario</h2>
              <div style={{ color: '#94a3b8', lineHeight: '1.8', fontSize: '16px' }}>
                <p style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#e2e8f0' }}>Step 1:</strong> An account's state becomes inconsistent—maybe a transaction failed partway through, leaving the account in a broken state.
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#e2e8f0' }}>Step 2:</strong> You need to fix the account by updating its state. However, you want to do this privately—you don't want to reveal all the account details publicly.
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#e2e8f0' }}>Step 3:</strong> Using private actions, you execute a recovery program that updates the account state. The program validates that the fix is correct and authorized.
                </p>
                <p style={{ marginBottom: '16px' }}>
                  <strong style={{ color: '#e2e8f0' }}>Step 4:</strong> A proof is generated showing that the account was fixed correctly, without revealing the private details of what was wrong or how it was fixed.
                </p>
                <p style={{ marginBottom: '0' }}>
                  <strong style={{ color: '#e2e8f0' }}>The Result:</strong> The account is now in a valid state, and everyone can verify the fix was done correctly, but the private details remain hidden.
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                className="sandbox-button"
                onClick={() => window.open('https://playground.miden.xyz', '_blank', 'noopener,noreferrer')}
                style={{
                  padding: '14px 32px',
                  background: 'rgba(249, 115, 22, 0.1)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '8px',
                  color: '#f97316',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(249, 115, 22, 0.15)';
                  e.target.style.borderColor = 'rgba(249, 115, 22, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(249, 115, 22, 0.1)';
                  e.target.style.borderColor = 'rgba(249, 115, 22, 0.3)';
                }}
              >
                Open official Miden Playground
              </button>
              <p style={{ color: '#94a3b8', marginTop: '16px', fontSize: '14px' }}>
                In the Playground, explore state management examples to see how account recovery works in code.
              </p>
            </div>
          </div>
        </section>
      );
    }

    // Render challenges
    if (currentChallenge === 'fibonacci-proof') {
      if (!FibonacciProofLesson) {
        if (process.env.NODE_ENV === 'development') {
          console.error('FibonacciProofLesson component not found');
        }
        return <div>Error: FibonacciProofLesson component not loaded</div>;
      }
      return (
        <FibonacciProofLesson onBack={handleBackToPlayground} />
      );
    }

    return (
      <section className="playground-page">
        <div className="playground-header">
          <div className="playground-header-content">
            <div className="playground-title-section">
              <p>Learn how private actions, secure receipts, and invisible math work through quizzes, games, and explanations.</p>
            </div>
          </div>
        </div>

        <div className="playground-tabs">
          {Object.keys(activities).map((tab) => {
            const tabIcons = {
              games: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2"/>
                </svg>
              ),
              tutorials: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              ),
              experiments: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              ),
              challenges: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                </svg>
              )
            };
            return (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                <span className="tab-icon">{tabIcons[tab]}</span>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            );
          })}
        </div>

        <div className="playground-content">
          <div className="activities-section">
            {(() => {
              const filteredActivities = activities[activeTab] || [];
              
              // games tab
              if (activeTab === 'games' && process.env.NODE_ENV === 'development') {
                console.log('Games tab activities:', filteredActivities);
                console.log('Logic Maze found:', filteredActivities.find(a => a.id === 'logic-maze'));
              }

              if (filteredActivities.length === 0) {
                return (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', background: 'rgba(15, 23, 42, 0.6)', borderRadius: '12px', border: '1px solid rgba(100, 116, 139, 0.3)' }}>
                    <p style={{ fontSize: '18px' }}>No activities found in "{activeTab}" tab.</p>
                  </div>
                );
              }

              return (
                <div className="activities-grid">
                  {filteredActivities.map((activity) => (
                <div key={activity.id} className="activity-card">
                  <div className="activity-header">
                    <div className="activity-icon">
                      {activity.icon}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div className="difficulty-badge" style={{ backgroundColor: getDifficultyColor(activity.difficulty) }}>
                        {activity.difficulty}
                      </div>
                      {activity.forDevs && (
                        <div className="dev-badge" style={{ 
                          backgroundColor: 'rgba(249, 115, 22, 0.2)', 
                          color: '#f97316',
                          border: '1px solid #f97316',
                          fontSize: '10px',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>
                          For devs
                        </div>
                      )}
                    </div>
                  </div>
                  <h3 className="activity-title">{activity.title}</h3>
                  <p className="activity-description">{activity.description}</p>
                  {activity.objective && (
                    <div className="activity-objective">
                      <span className="objective-label">Goal:</span>
                      <span className="objective-text">{activity.objective}</span>
                    </div>
                  )}
                  <div className="activity-meta">
                    {activity.timeEstimate && (
                      <span className="time-estimate">⏱ {activity.timeEstimate}</span>
                    )}
                    {activity.concept && (
                      <span className="concept-badge">{activity.concept}</span>
                    )}
                  </div>
                  <div className="activity-progress">
                    <span className="progress-pill">{activity.progress}</span>
                  </div>
                  <div className="activity-buttons">
                    <button 
                      className="activity-button"
                      onClick={() => handleGameClick(activity.id)}
                    >
                      {activity.progress === 'Completed' ? 'Review' : 
                       activity.progress === 'Not started' ? 'Start' :
                       activity.progress === 'Sandbox' ? 'Open' : 'Continue'}
                    </button>
                  </div>
                </div>
              ))}
                </div>
              );
            })()}
          </div>

          <div className="playground-sidebar">
            <div className="progress-section">
              <ProgressIndicator />
            </div>
            
            <div className="leaderboard-section">
              <h3 className="section-title">Coming Soon</h3>
              <div className="leaderboard-coming-soon">
                <div className="coming-soon-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4 className="coming-soon-title">Something Fun Coming Up</h4>
                <p className="coming-soon-text">Stay Tuned</p>
              </div>
              
              {/* <div className="leaderboard">
                {leaderboard.map((user) => (
                  <div key={user.rank} className="leaderboard-item">
                    <div className="leader-info">
                      <span className="rank">#{user.rank}</span>
                      <span className="username">{user.name}</span>
                      <span className="user-score">{user.score} pts</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${user.progress}%` }}
                      ></div>
                    </div>
                    <span className="user-badges">{user.badges} badges</span>
                  </div>
                ))}
              </div> */}
            </div>
          </div>

        </div>
      </section>
    );
  }

  // For non-playground page usage, show a hero/CTA
  return (
    <section className="playground-section">
      <div className="playground-hero" style={{
        padding: '60px 40px',
        textAlign: 'center',
        background: 'rgba(15, 23, 42, 0.6)',
        border: '1px solid rgba(100, 116, 139, 0.3)',
        borderRadius: '12px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2 style={{ 
          color: '#e2e8f0', 
          marginBottom: '16px',
          fontSize: '28px',
          fontWeight: '700',
          lineHeight: '1.3'
        }}>
          Play with private actions, secure receipts, and invisible math.
        </h2>
        <p style={{ 
          color: '#94a3b8', 
          marginBottom: '32px',
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          Short games and quizzes. No code needed.
        </p>
        {setCurrentPage && (
          <button 
            onClick={() => setCurrentPage('playground')}
            style={{
              padding: '14px 32px',
              background: 'rgba(249, 115, 22, 0.1)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              borderRadius: '8px',
              color: '#f97316',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(249, 115, 22, 0.15)';
              e.target.style.borderColor = 'rgba(249, 115, 22, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(249, 115, 22, 0.1)';
              e.target.style.borderColor = 'rgba(249, 115, 22, 0.3)';
            }}
          >
            Open Playground
          </button>
        )}
      </div>
    </section>
  );
};

export default Playground;